import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import { getAllOffers } from 'selectors/offerSelectors'
import { createSelector } from 'reselect'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import getObjectKey from 'lib/object/getObjectKey'
import { isLEHotelItem } from 'lib/checkout/checkoutUtils'
import { AGENT_HUB_PAYMENT_MODE } from 'constants/payment'
import { checkoutWithMemberPrice } from 'checkout/selectors/view/luxPlusSubscription'
import { getInsuranceTotals } from '../insurance'
import getAllItemsDiscountTotals, { getAllItemsVoucherTotals } from '../getAllItemsDiscountTotals'
import getAllItemViews from 'checkout/selectors/view/getAllItemViews'
import getExperienceItemsView from 'checkout/selectors/view/itemViews/getExperienceItemsView'
import { getAccommodationBreakdownView, checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import getLoyaltyLoungePassItemViews from 'checkout/selectors/view/itemViews/getLoyaltyLoungePassItemViews'
import getLoyaltyTransferItemView from 'checkout/selectors/view/itemViews/getLoyaltyTransferItemView'
import { buildPaymentScheduleItems } from '../paymentSchedule/paymentScheduleItemUtils'
import { getCartOfferCountryCode } from 'checkout/lib/payment/isDomesticOrder'
import isGroupPaymentEligible from './isGroupPaymentEligible'

export const getGroupPaymentPreviewRequestInfo = createSelector(
  isGroupPaymentEligible,
  (state: App.State) => getInsuranceTotals(state),
  (state: App.State) => getAllItemsDiscountTotals(state),
  (state: App.State) => getAllItemsVoucherTotals(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getAllOffers(state),
  (state: App.State) => getAllItemViews(state),
  (state: App.State) => getExperienceItemsView(state),
  (state: App.State) => getAccommodationBreakdownView(state),
  (state: App.State) => state.checkout.commissionData?.itemsCommission,
  (state: App.State) => state.checkout.payment.agentHubPaymentMode,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => getLoyaltyLoungePassItemViews(state),
  (state: App.State) => getLoyaltyTransferItemView(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => state.checkout.accommodationSessions,
  (
    available,
    insuranceTotals,
    discountTotalToCartItemsMap,
    voucherTotalToCartItemsMap,
    checkoutWithMemberPrice,
    cartItems,
    offers,
    itemViews,
    experienceItemViews,
    accommodationBreakdownView,
    itemsCommission,
    agentHubPaymentMode,
    currency,
    loyaltyLoungePassItemViews,
    loyaltyTransferItemViews,
    accommodationOfferView,
    accommodationSessions,
  ): App.Checkout.GroupPaymentPreviewRequestInfo | null => {
    if (!available) return null

    const startDate = accommodationOfferView.data[0]?.startDate
    if (!startDate) return null

    const items = buildPaymentScheduleItems(cartItems, {
      offers,
      discountTotalToCartItemsMap,
      checkoutWithMemberPrice,
      insuranceTotals,
      accommodationBreakdownView,
      itemViews,
      experienceItemViews,
      loyaltyLoungePassItemViews,
      loyaltyTransferItemViews,
      agentHubCommission: {
        shouldUse: isAgentHubEnabled && agentHubPaymentMode === AGENT_HUB_PAYMENT_MODE.PAY_NET_AMOUNT,
        itemsCommission,
        voucherTotalToCartItemsMap,
      },
      accommodationSessions,
    })

    if (!items) return null

    const hotelItem = cartItems.find(isLEHotelItem)
    if (!hotelItem) return null

    const offerCountryCode = getCartOfferCountryCode(cartItems, offers)

    const bookingInfo: App.Checkout.PaymentScheduleRequestInfo['bookingInfo'] = {
      bookingDate: moment().format(ISO_DATE_FORMAT),
      checkinDate: startDate,
      supplierDueDate: startDate,
      isCreditApplied: false,
      isGiftCardApplied: false,
      giftCardAmount: 0,
      isPaymentScheduleRebooking: false,
      offerCountryCode,
      currency,
      items,
    }
    const allocatableTotal = items.reduce(
      (total, item) => total + item.total - item.discountTotal,
      0,
    )

    return {
      bookingInfo,
      allocatableTotal: Math.round((allocatableTotal + Number.EPSILON) * 100) / 100,
      fingerprint: getObjectKey(bookingInfo),
    }
  },
)

export default getGroupPaymentPreviewRequestInfo
